import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import {
  countWords,
  readPresentation,
  titlePlaceholder,
  type SlideInfo,
} from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

/** Folien, auf denen eine Tabelle oder ein Diagramm liegt. */
function datenfolien(slides: SlideInfo[]): SlideInfo[] {
  return slides.filter((folie) =>
    folie.shapes.some(
      (form) =>
        form.type === "Table" ||
        form.type === "Chart" ||
        form.placeholder?.containedType === "Table" ||
        form.placeholder?.containedType === "Chart",
    ),
  );
}

export const lektionAussage: Lesson = {
  id: "aussage",
  title: "Der Titel sagt, was die Zahlen zeigen",
  summary: "Die wirksamste Verbesserung an jeder Datenfolie kostet zehn Sekunden.",
  durationMinutes: 8,
  learningGoals: [
    "Datenfolien mit einem Aussagetitel versehen",
    "Zahlen auf das lesbare Maß runden",
  ],
  steps: [
    {
      id: "aussagetitel",
      title: "Aussage statt Kategorie",
      explanation: [
        "Über den meisten Diagrammen steht ein Titel wie „Umsatzentwicklung“. Das ist " +
          "korrekt und hilft niemandem – es sagt nur, worum es geht, nicht was " +
          "herauskommt.",
        "Ein Aussagetitel nimmt das Ergebnis vorweg: „Umsatz wächst seit drei Quartalen“. " +
          "Das Publikum weiß dann sofort, worauf es im Diagramm achten soll, und findet " +
          "den passenden Verlauf in Sekundenbruchteilen statt in einer halben Minute.",
        "Der Effekt ist größer, als man vermutet. Wer eine Präsentation später " +
          "durchblättert – und das tun die meisten Empfänger –, kann allein aus den " +
          "Titeln den roten Faden lesen. Aus einer Folge von Kategorien wird eine " +
          "Argumentation.",
        "Die Probe aufs Exempel: Lies nur die Titel deiner Folien hintereinander. Ergibt " +
          "das eine Geschichte, stimmen die Titel. Ergibt es eine Inhaltsübersicht, sind " +
          "es Kategorien.",
      ],
      keyPoints: [
        "„Umsatz wächst seit drei Quartalen“ statt „Umsatzentwicklung“.",
        "In der Gliederungsansicht lassen sich alle Titel am Stück lesen.",
        "Die Titelfolge sollte eine Geschichte ergeben, keine Inhaltsübersicht.",
      ],
      task: {
        instruction:
          "Gib jeder Folie mit Tabelle oder Diagramm einen Titel, der die Aussage nennt " +
          "und nicht nur die Kategorie – als Anhaltspunkt mindestens drei Wörter.",
        hint: "„Umsatz wächst um 12 %“ statt „Umsatz“.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar(
              "Die Art einer Form und der Text der Platzhalter",
              "1.4",
              "Lies deine Folientitel selbst durch: Nennen sie die Aussage oder nur das Thema?",
            );
          }

          const folien = datenfolien(snapshot.slides);
          if (folien.length === 0) {
            return fail(
              "Es gibt noch keine Folie mit einer Tabelle oder einem Diagramm.",
              "Füge zuerst eine Tabelle oder ein Diagramm ein.",
            );
          }

          // Die Wortzahl ist nur ein Anhaltspunkt: Sie schließt „Umsatz“ aus,
          // kann aber nicht beurteilen, ob wirklich eine Aussage dasteht.
          const knapp = folien.filter((folie) => {
            const titel = titlePlaceholder(folie);
            const text = titel?.paragraphs[0] ?? "";
            return countWords(text) < 3;
          });

          if (knapp.length === 0) {
            const beispiel = titlePlaceholder(folien[0])?.paragraphs[0] ?? "";
            return pass(
              `Alle ${folien.length} Datenfolien tragen einen mehrteiligen Titel, zum Beispiel „${beispiel}“. ` +
                "Ob darin wirklich die Aussage steht, entscheidest du – die Wortzahl ist nur ein Anhaltspunkt.",
            );
          }

          const erste = knapp[0];
          const titel = titlePlaceholder(erste)?.paragraphs[0];
          return fail(
            titel
              ? `Der Titel von Folie ${erste.index + 1} lautet nur „${titel}“ – das nennt das Thema, nicht das Ergebnis.`
              : `Folie ${erste.index + 1} hat noch gar keinen Titel.`,
            "Formuliere den Titel als vollständige Aussage: Was soll man aus diesen Zahlen mitnehmen?",
          );
        },
      },
    },
    {
      id: "zahlen-runden",
      title: "Zahlen auf das lesbare Maß bringen",
      explanation: [
        "„1.247.983 €“ ist genau und unlesbar. „1,25 Mio. €“ ist ungenau und sofort " +
          "erfasst. Auf einer Folie gewinnt fast immer die zweite Variante, weil es dort " +
          "um die Größenordnung geht und nicht um den Cent.",
        "Als Faustregel reichen zwei bis drei signifikante Stellen. Prozentwerte ohne " +
          "Nachkommastelle, Geldbeträge in Tausend oder Millionen, Zeiträume in ganzen " +
          "Monaten. Die genauen Werte gehören in die Anlage oder die Sprechernotizen – " +
          "dort stören sie niemanden und stehen bereit, falls jemand nachfragt.",
        "Wichtig ist Einheitlichkeit: Innerhalb einer Präsentation immer dieselbe " +
          "Rundung, dieselbe Einheit und dasselbe Zahlenformat. Ein Wechsel zwischen " +
          "„1,2 Mio.“ und „1.200 Tsd.“ auf zwei aufeinanderfolgenden Folien kostet mehr " +
          "Aufmerksamkeit als die Zahl wert ist.",
      ],
      keyPoints: [
        "Zwei bis drei signifikante Stellen genügen auf einer Folie.",
        "Einheit und Rundung in der gesamten Präsentation gleich halten.",
        "Genaue Werte in die Sprechernotizen oder die Anlage.",
      ],
    },
  ],
};
